import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ArchiveProjectImage, ImprovementProjectImage, TaskImage, UserProfileImage } from '../models/image';
import { ProjectFile } from '../types/improvement-project';
import { JobFile } from '../types/job';
import { TaskFile } from '../types/task';

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private readonly CACHE_SIZE = 1;

  constructor(private http: HttpClient) {}

  getImagesByTaskId(jobTaskId: string): Observable<TaskImage[]> {
    const images$ = this.http.get<TaskImage[]>(environment.apiDomain + `/media/images?taskId=${jobTaskId}`);
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  getImprovementProjectImagesByJobId(jobId: string): Observable<ImprovementProjectImage[]> {
    const images$ = this.http.get<ImprovementProjectImage[]>(
      environment.apiDomain + `/media/images/job/${jobId}/improvement-project-images`,
    );
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  getImprovementProjectImagesByProjectId(projectId: string): Observable<ImprovementProjectImage[]> {
    const images$ = this.http.get<ImprovementProjectImage[]>(
      environment.apiDomain + `/media/images/improvement-project/${projectId}`,
    );
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  getArchiveProjectImageByEnterpriseId(enterpriseId: string): Observable<ArchiveProjectImage[]> {
    const images$ = this.http.get<ArchiveProjectImage[]>(
      environment.apiDomain + `/media/images/archive-project/${enterpriseId}`,
    );
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  getImageByTaskIdAndStage(jobTaskId: string, improvementStage: string): Observable<TaskImage[]> {
    const images$ = this.http.get<TaskImage[]>(
      environment.apiDomain + `/media/images?taskId=${jobTaskId}&workplaceImprovementStage=${improvementStage}`,
    );
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  deleteImage(imageId: string): Observable<any> {
    return this.http.delete(environment.apiDomain + '/media/images/' + imageId);
  }

  deleteImprovementProjectImage(imageId: string): Observable<any> {
    return this.http.delete(environment.apiDomain + '/media/images/improvement-project/' + imageId);
  }

  deleteArchiveProjectImageByArchiveProjectId(archiveProjectId: string): Observable<any> {
    return this.http.delete(environment.apiDomain + '/media/images/archive-project/' + archiveProjectId);
  }

  getEnterpriseLogo(enterpriseId: string): Observable<any> {
    const images$ = this.http.get(environment.apiDomain + `/media/images/enterprises/${enterpriseId}/logo`);
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  deleteEnterpriseLogo(enterpriseId: string): Observable<any> {
    return this.http.delete(environment.apiDomain + `/media/images/enterprises/${enterpriseId}/logo`);
  }

  getUserProfileImageByUserId(userId: string): Observable<UserProfileImage> {
    const images$ = this.http.get<UserProfileImage>(environment.apiDomain + `/media/images/user/${userId}`);
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  getUserProfileImagesByUserIds(userIds: string[]): Observable<UserProfileImage[]> {
    const images$ = this.http.get<UserProfileImage[]>(environment.apiDomain + `/media/images/users`, {
      params: {
        userIds: userIds?.join(',') || '',
      },
    });
    return images$.pipe(shareReplay(this.CACHE_SIZE));
  }

  deleteUserProfileImageByUserId(userId: string): Observable<any> {
    return this.http.delete(environment.apiDomain + `/media/images/user/${userId}`);
  }

  addImprovementProjectImageFromUrl(
    enterpriseId: string,
    jobId: string,
    improvementProjectId: string,
    imageUrl: string,
    coverImage: boolean,
  ): Observable<any> {
    return this.http.post(
      `${environment.apiDomain}/media/images/enterprise/${enterpriseId}/job/${jobId}/improvement-project/${improvementProjectId}/url?coverImage=${coverImage}`,
      imageUrl,
    );
  }

  getJobFilesByJobId(jobId: string): Observable<JobFile[]> {
    return this.http.get<JobFile[]>(environment.apiDomain + `/media/job-files/${jobId}`);
  }

  deleteJobFile(fileId: string) {
    return this.http.delete(environment.apiDomain + `/media/job-files/${fileId}`);
  }

  getTaskFilesByTaskId(taskId: string): Observable<TaskFile[]> {
    return this.http.get<TaskFile[]>(environment.apiDomain + `/media/task-files/${taskId}`);
  }

  deleteTaskFile(fileId: string) {
    return this.http.delete(environment.apiDomain + `/media/task-files/${fileId}`);
  }

  getProjectFilesByProjectId(projectId: string): Observable<ProjectFile[]> {
    return this.http.get<ProjectFile[]>(environment.apiDomain + `/media/project-files/${projectId}`);
  }

  deleteProjectFile(fileId: string) {
    return this.http.delete(environment.apiDomain + `/media/project-files/${fileId}`);
  }
}
